import type { FormEvent } from "react";
import { useState } from "react";
import { API_ROUTES, type LoginInput, type LoginResult } from "@shared/api";
import { apiPost } from "@client/api/client";
import { setToken } from "@client/api/token";

export function LoginPage({ onLoggedIn }: { onLoggedIn: () => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      const result = await apiPost<LoginResult, LoginInput>(API_ROUTES.authLogin, {
        username,
        password,
      });
      setToken(result.token);
      onLoggedIn();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo iniciar sesión");
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden px-4">
      <div className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-primary/20 blur-3xl" />
      <div className="pointer-events-none absolute -right-32 -bottom-32 h-96 w-96 rounded-full bg-secondary/20 blur-3xl" />

      <form
        onSubmit={onSubmit}
        className="card animate-pop z-10 w-full max-w-sm bg-base-100 shadow-2xl"
      >
        <div className="card-body gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-content shadow-lg">
              <svg viewBox="0 0 24 24" className="h-6 w-6" fill="currentColor" aria-hidden>
                <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 2a8 8 0 1 1-4.2 14.8l-.4-.2-2.6.7.7-2.5-.2-.4A8 8 0 0 1 12 4Zm-3.2 4.2c-.2 0-.5 0-.7.3-.2.3-.9.9-.9 2.1s.9 2.4 1 2.6c.1.2 1.7 2.8 4.2 3.8 2.1.8 2.5.7 3 .6.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.2-1.2-.1-.1-.3-.2-.6-.4l-1.8-.9c-.2-.1-.4-.1-.6.1l-.6.8c-.1.2-.2.2-.4.1-.2-.1-.9-.3-1.7-1.1-.6-.6-1-1.2-1.1-1.4-.1-.2 0-.3.1-.4l.4-.5c.1-.2.1-.3.1-.5v-.5c-.2-.5-.7-1.4-.9-1.9-.1-.4-.3-.4-.5-.4h-.1Z" />
              </svg>
            </div>
            <div>
              <h1 className="text-lg font-bold leading-tight">WhatsApp Bot</h1>
              <p className="text-xs text-base-content/50">Panel multi-sesión</p>
            </div>
          </div>

          <label className="form-control w-full">
            <span className="label-text mb-1 text-xs font-medium">Usuario</span>
            <input
              autoFocus
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              placeholder="admin"
              autoComplete="username"
              className="input input-bordered w-full transition-all duration-200 focus:scale-[1.01]"
            />
          </label>

          <label className="form-control w-full">
            <span className="label-text mb-1 text-xs font-medium">Contraseña</span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              className="input input-bordered w-full transition-all duration-200 focus:scale-[1.01]"
            />
          </label>

          {error ? (
            <div role="alert" className="alert alert-error animate-fade-in py-2 text-sm">
              <span>{error}</span>
            </div>
          ) : null}

          <button
            type="submit"
            disabled={pending || !username || !password}
            className="btn btn-primary w-full transition-transform duration-200 hover:scale-[1.02] active:scale-95"
          >
            {pending ? <span className="loading loading-spinner loading-sm" /> : null}
            {pending ? "Entrando…" : "Entrar"}
          </button>
        </div>
      </form>
    </main>
  );
}
